import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2ad99d46-1dec-45f4-a987-3453d8d7d4a7/artifacts/h5sqzqza_7.png";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login, user, error } = useAuth();
  const nav = useNavigate();

  useEffect(() => { if (user) nav("/admin/leads"); }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    const ok = await login(email, password);
    setSubmitting(false);
    if (ok) nav("/admin/leads");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-950 text-white relative overflow-hidden">
        <div className="grain absolute inset-0"/>
        <div className="relative flex items-center gap-3">
          <img src={LOGO_URL} alt="TARA" className="h-12 w-12 rounded-xl"/>
          <div>
            <div className="font-display font-extrabold text-xl">TARA Finserv</div>
            <div className="text-xs uppercase tracking-[0.22em] text-saffron">Admin Console</div>
          </div>
        </div>
        <div className="relative">
          <div className="overline text-saffron">Control room</div>
          <h2 className="font-display text-4xl xl:text-5xl font-extrabold mt-3">Lead management, navigation builder, content & brand controls — all in one place.</h2>
        </div>
        <div className="relative text-sm text-slate-400">Restricted access. All activity is logged.</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={onSubmit} autoComplete="off" className="w-full max-w-md card-flat p-8 sm:p-10 relative" data-testid="admin-login-form">
          <Link to="/" data-testid="admin-login-back" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted hover:text-saffron transition-colors mb-5">
            <ArrowLeft className="h-3.5 w-3.5"/> Back to home
          </Link>
          {/* dummy fields to block browsers from autofilling the real inputs */}
          <input type="text" name="fake-user" autoComplete="username" tabIndex={-1} aria-hidden="true" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}/>
          <input type="password" name="fake-pass" autoComplete="new-password" tabIndex={-1} aria-hidden="true" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}/>

          <div className="overline">Sign in</div>
          <h1 className="font-display text-3xl font-extrabold mt-2">Welcome back.</h1>
          <p className="text-ink-muted text-sm mt-2">Use your TARA admin credentials.</p>

          <div className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-ink-muted">Email</Label>
              <Input data-testid="admin-login-email" type="email" name={`admin-email-${Math.random().toString(36).slice(2,8)}`} autoComplete="off" autoCorrect="off" spellCheck="false"
                readOnly onFocus={(e)=>e.target.removeAttribute("readonly")}
                value={email} onChange={e=>setEmail(e.target.value)} required/>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-ink-muted">Password</Label>
              <Input data-testid="admin-login-password" type="password" name={`admin-pass-${Math.random().toString(36).slice(2,8)}`} autoComplete="new-password"
                readOnly onFocus={(e)=>e.target.removeAttribute("readonly")}
                value={password} onChange={e=>setPassword(e.target.value)} required/>
            </div>
          </div>

          {error && <div className="mt-4 text-sm text-red-600" data-testid="admin-login-error">{error}</div>}

          <Button data-testid="admin-login-submit" type="submit" disabled={submitting || !email || !password} className="btn-saffron rounded-full w-full mt-6 h-12">
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin"/>}
            Sign in
          </Button>
        </form>
      </div>
    </div>
  );
}
